import React, { useState, useEffect } from 'react';
import { 
	CreditCard, 
	User, 
	Calendar, 
	Package, 
	Truck, 
	CheckCircle, 
	AlertCircle, 
	Clock, 
	RefreshCw,
	Eye,
	XCircle,
	ArrowRight,
	MapPin,
	Phone,
	Mail,
	Send
} from 'lucide-react';
import { apiService, purchaseService, dressStatusService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const PurchasedItems = () => {
	const { user } = useAuth();
	const [purchases, setPurchases] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedStatus, setSelectedStatus] = useState('all');
	const [selectedPurchase, setSelectedPurchase] = useState(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const [sendingEmail, setSendingEmail] = useState(false);
	const [showDispatchModal, setShowDispatchModal] = useState(false);
	const [dispatchForm, setDispatchForm] = useState({
		awb_number: '',
		confirm_awb_number: '',
		courier_provider: ''
	});
	const [dispatching, setDispatching] = useState(false);

	// Add CSS animation for spinning icon
	React.useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `
			@keyframes spin {
				from { transform: rotate(0deg); }
				to { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		
		return () => {
			document.head.removeChild(style);
		};
	}, []);

    // Dress status options fetched from API
    const [dressStatusOptions, setDressStatusOptions] = useState([
        { value: 'all', label: 'All Orders', color: '#6B7280' }
    ]);

    const fetchDressStatuses = async () => {
        try {
            const res = await dressStatusService.getAll();
            if (res?.success && Array.isArray(res.data)) {
                const mapped = res.data.map(s => ({
                    value: String(s.id),
                    label: s.name,
                    color: s.color || '#6B7280'
                }));
                setDressStatusOptions([{ value: 'all', label: 'All Orders', color: '#6B7280' }, ...mapped]);
            }
        } catch (e) {
            console.error('Failed to fetch dress statuses:', e);
        }
    };

	// Fetch purchases from API
	const fetchPurchases = async () => {
		try {
			setLoading(true);
			// Always fetch all purchases, we'll filter by dress status on frontend
			const response = await apiService.get('/admin/purchases');
			
			if (response.success) {
				let purchases = response.data.data || [];
				
				// Filter by dress status if not 'all'
				if (selectedStatus !== 'all') {
					purchases = purchases.filter(purchase => 
						purchase.listing?.dress_status_id?.toString() === selectedStatus
					);
				}
				
				setPurchases(purchases);
			} else {
				toast.error('Failed to fetch purchases');
			}
		} catch (error) {
			console.error('Error fetching purchases:', error);
			toast.error('Error fetching purchases: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	// Fetch purchase details
	const fetchPurchaseDetails = async (orderId) => {
		try {
			const response = await apiService.get(`/admin/purchases/${orderId}`);
			if (response.success) {
				console.log('Purchase details response:', response.data);
				console.log('Listing data:', response.data.listing);
				console.log('Dress status data:', response.data.listing?.dressStatus);
				setSelectedPurchase(response.data);
				setShowDetailsModal(true);
			} else {
				toast.error('Failed to fetch purchase details');
			}
		} catch (error) {
			console.error('Error fetching purchase details:', error);
			toast.error('Error fetching purchase details: ' + error.message);
		}
	};

	// Dispatch to buyer - Manual AWB assignment by Store Manager
	const handleDispatchToBuyer = async () => {
		if (!dispatchForm.awb_number || !dispatchForm.confirm_awb_number || !dispatchForm.courier_provider) {
			toast.error('Please fill in all fields');
			return;
		}

		if (dispatchForm.awb_number !== dispatchForm.confirm_awb_number) {
			toast.error('AWB numbers do not match');
			return;
		}

		try {
			setDispatching(true);
			toast.loading('Dispatching order to buyer...', { id: 'dispatch-to-buyer' });
			
			const response = await purchaseService.dispatchToBuyer(selectedPurchase.order_id, {
				awb_number: dispatchForm.awb_number,
				confirm_awb_number: dispatchForm.confirm_awb_number,
				courier_provider: dispatchForm.courier_provider
			});

			if (response.success) {
				toast.success('Order dispatched to buyer successfully!', { id: 'dispatch-to-buyer' });
				setShowDispatchModal(false);
				setDispatchForm({ awb_number: '', confirm_awb_number: '', courier_provider: '' });
				// Refresh purchase details
				await fetchPurchaseDetails(selectedPurchase.order_id);
				// Refresh purchases list
				await fetchPurchases();
			} else {
				toast.error(response.message || 'Failed to dispatch order', { id: 'dispatch-to-buyer' });
			}
		} catch (error) {
			console.error('Error dispatching to buyer:', error);
			toast.error('Error dispatching order: ' + error.message, { id: 'dispatch-to-buyer' });
		} finally {
			setDispatching(false);
		}
	};

	// Send AWB number to seller via email (automatically assigns AWB if needed)
	const sendAwbEmail = async (orderId) => {
		try {
			setSendingEmail(true);
			
			toast.loading('Processing AWB assignment and sending email...', { id: 'send-awb-email' });
			const response = await purchaseService.sendAwbToSeller(orderId);
			
			if (response.success) {
				toast.success('AWB number assigned and sent to seller successfully!', { id: 'send-awb-email' });
				// Refresh purchase details to get updated information
				await fetchPurchaseDetails(orderId);
			} else {
				toast.error(response.message || 'Failed to assign AWB and send email', { id: 'send-awb-email' });
			}
		} catch (error) {
			console.error('Error sending AWB email:', error);
			toast.error('Error sending AWB email: ' + error.message);
		} finally {
			setSendingEmail(false);
		}
	};

	// Mark item as received at store
	const markItemReceived = async (orderId) => {
		try {
			toast.loading('Marking item as received at store...', { id: 'mark-received' });
			const response = await purchaseService.markItemReceived(orderId);
			if (response.success) {
				toast.success('Item marked as received at store successfully!', { id: 'mark-received' });
				// Refresh the purchase details
				await fetchPurchaseDetails(orderId);
				// Refresh the purchases list
				await fetchPurchases();
			} else {
				toast.error(response.message || 'Failed to mark item as received', { id: 'mark-received' });
			}
		} catch (error) {
			console.error('Error marking item as received:', error);
			toast.error('Error marking item as received: ' + error.message);
		}
	};

	// Get dress status color
	const getDressStatusColor = (dressStatusId) => {
		const statusOption = dressStatusOptions.find(option => option.value === dressStatusId?.toString());
		return statusOption ? statusOption.color : '#6B7280';
	};

	// Get dress status icon
	const getDressStatusIcon = (dressStatusId) => {
		const iconMap = {
			'1': Clock,        // Pending
			'2': Package,      // Listed
			'3': Truck,        // In Transit
			'4': CheckCircle,  // At Store
			'5': CheckCircle,  // Authenticated
			'6': XCircle,      // Rejected
			'7': AlertCircle,  // Disposed
			'8': ArrowRight,   // Returned to Seller
			'9': XCircle       // De Listed
		};
		return iconMap[dressStatusId?.toString()] || AlertCircle;
	};

	// Get dress status name
	const getDressStatusName = (dressStatus) => {
		if (!dressStatus) return 'Unknown';
		if (typeof dressStatus === 'object') {
			return dressStatus?.name || 'Unknown';
		}
		const match = dressStatusOptions.find(option => option.value === dressStatus?.toString());
		return match?.label || 'Unknown';
	};

	// Format currency
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0
		}).format(amount);
	};

	// Format date
	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	// Helper function to get full image URL
	const getImageUrl = (path) => {
		if (!path) return null;
		// If path already starts with http, return as is
		if (path.startsWith('http')) return path;
		// Otherwise prepend the storage base URL
		const baseUrl = process.env.REACT_APP_API_URL || 'http://carsandbids-api.test';
		return `${baseUrl}/storage/${path}`;
	};

	// Calculate counts for each status
	const getStatusCounts = () => {
		const counts = {};
		
		// Initialize all status counts to 0
		dressStatusOptions.forEach(option => {
			if (option.value !== 'all') {
				counts[option.value] = 0;
			}
		});
		
		// Count purchases by status
		purchases.forEach(purchase => {
			const statusId = purchase.listing?.dress_status_id?.toString();
			if (statusId && counts.hasOwnProperty(statusId)) {
				counts[statusId]++;
			}
		});
		
		// Add total count
		counts.all = purchases.length;
		
		return counts;
	};

	// Handle status filter change
	const handleStatusFilter = (status) => {
		setSelectedStatus(status);
	};

    useEffect(() => {
        fetchDressStatuses();
    }, []);

    useEffect(() => {
        fetchPurchases();
    }, [selectedStatus]);

	return (
		<div style={{ padding: '24px' }}>
			{/* Header */}
			<div style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				marginBottom: '24px'
			}}>
				<h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
					Purchased Items
				</h1>
				<button
					onClick={() => fetchPurchases()}
					className="btn btn-secondary"
					disabled={loading}
					title="Refresh Purchases"
				>
					<RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
					Refresh
				</button>
			</div>

			{/* Dress Status Filter Tabs */}
			<div style={{
				display: 'flex',
				gap: '8px',
				marginBottom: '24px',
				flexWrap: 'wrap'
			}}>
				{dressStatusOptions.map((option) => {
					const statusCounts = getStatusCounts();
					const count = statusCounts[option.value] || 0;
					
					return (
						<button
							key={option.value}
							onClick={() => handleStatusFilter(option.value)}
							style={{
								padding: '8px 16px',
								borderRadius: '20px',
								border: 'none',
								backgroundColor: selectedStatus === option.value ? option.color : '#F3F4F6',
								color: selectedStatus === option.value ? 'white' : '#374151',
								fontSize: '14px',
								fontWeight: '500',
								cursor: 'pointer',
								transition: 'all 0.2s',
								display: 'flex',
								alignItems: 'center',
								gap: '6px'
							}}
						>
							{option.label}
							<span style={{
								backgroundColor: selectedStatus === option.value ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
								color: selectedStatus === option.value ? 'white' : '#374151',
								padding: '2px 8px',
								borderRadius: '12px',
								fontSize: '12px',
								fontWeight: '600',
								minWidth: '20px',
								textAlign: 'center'
							}}>
								{count}
							</span>
						</button>
					);
				})}
			</div>

			{/* Loading State */}
			{loading && (
				<div style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '40px',
					fontSize: '16px',
					color: '#6B7280'
				}}>
					<RefreshCw size={20} className="animate-spin" style={{ marginRight: '8px' }} />
					Loading purchases...
				</div>
			)}

			{/* Purchases Table */}
			{!loading && (
				<div style={{ overflowX: 'auto' }}>
					<table className="table">
						<thead>
							<tr>
								<th style={{ width: '120px' }}>Order ID</th>
								<th style={{ width: '100px' }}>Image</th>
								<th>Item Details</th>
								<th>Buyer</th>
								<th>Seller</th>
								<th>Amount</th>
								<th>Dress Status</th>
								<th>Created</th>
								<th style={{ width: '100px' }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{purchases.length === 0 ? (
								<tr>
									<td colSpan="9" style={{
										textAlign: 'center',
										padding: '40px',
										color: '#6B7280',
										fontSize: '16px'
									}}>
										No purchases found
									</td>
								</tr>
							) : (
								purchases.map((purchase) => {
									const DressStatusIcon = getDressStatusIcon(purchase.listing?.dress_status_id);
									return (
										<tr key={purchase.id}>
											{/* Order ID */}
											<td>
												<div style={{
													fontFamily: 'monospace',
													fontSize: '12px',
													fontWeight: '600',
													color: '#374151'
												}}>
													{purchase.order_id}
												</div>
											</td>

											{/* Image */}
											<td>
												{purchase.listing?.dress_images?.find(img => img.image_type === 'front_full')?.thumbnail_path ? (
													<img
														src={getImageUrl(purchase.listing.dress_images.find(img => img.image_type === 'front_full').thumbnail_path)}
														alt="Item Front View"
														style={{
															width: '60px',
															height: '60px',
															objectFit: 'cover',
															borderRadius: '8px',
															border: '1px solid #E5E7EB'
														}}
													/>
												) : purchase.listing?.dress_images?.[0]?.thumbnail_path ? (
													<img
														src={getImageUrl(purchase.listing.dress_images[0].thumbnail_path)}
														alt="Item"
														style={{
															width: '60px',
															height: '60px',
															objectFit: 'cover',
															borderRadius: '8px',
															border: '1px solid #E5E7EB'
														}}
													/>
												) : (
													<div style={{
														width: '60px',
														height: '60px',
														backgroundColor: '#F3F4F6',
														borderRadius: '8px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: '#9CA3AF'
													}}>
														<Package size={20} />
													</div>
												)}
											</td>

											{/* Item Details */}
											<td>
												<div>
													<div style={{
														fontWeight: '600',
														fontSize: '14px',
														color: '#111827',
														marginBottom: '4px'
													}}>
														{purchase.listing?.item_details?.item_name || 'N/A'}
													</div>
													<div style={{
														fontSize: '12px',
														color: '#6B7280'
													}}>
														{purchase.listing?.item_details?.brand || 'N/A'} • {purchase.listing?.item_details?.size || 'N/A'}
													</div>
												</div>
											</td>

											{/* Buyer */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}>
													<User size={14} color="#6b7280" />
													<span style={{ fontSize: '14px' }}>
														{purchase.buyer?.name || 'N/A'}
													</span>
												</div>
											</td>

											{/* Seller */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}>
													<User size={14} color="#6b7280" />
													<span style={{ fontSize: '14px' }}>
														{purchase.seller?.full_name || 'N/A'}
													</span>
												</div>
											</td>

											{/* Amount */}
											<td>
												<div style={{
													fontWeight: '600',
													fontSize: '14px',
													color: '#059669'
												}}>
													{formatCurrency(purchase.total_amount)}
												</div>
												<div style={{
													fontSize: '12px',
													color: '#6B7280'
												}}>
													Deposit: {formatCurrency(purchase.deposit_amount)}
												</div>
											</td>

											{/* Dress Status */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
													backgroundColor: getDressStatusColor(purchase.listing?.dress_status_id),
													color: 'white',
													padding: '4px 8px',
													borderRadius: '12px',
													fontSize: '12px',
													fontWeight: '500',
													width: 'fit-content'
												}}>
													<DressStatusIcon size={12} />
													{getDressStatusName(
														purchase.listing?.dress_status ||
														purchase.listing?.dressStatus ||
														purchase.listing?.dress_status_id ||
														purchase.listing?.dressStatusId
													)}
												</div>
											</td>
											{/* Created Date */}
											<td>
												<div style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px'
												}}>
													<Calendar size={12} color="#6b7280" />
													<span style={{ fontSize: '12px', color: '#6b7280' }}>
														{formatDate(purchase.created_at)}
													</span>
												</div>
											</td>

											{/* Actions */}
											<td>
												<div style={{
													display: 'flex',
													gap: '6px'
												}}>
													<button
														onClick={() => fetchPurchaseDetails(purchase.order_id)}
														className="btn btn-secondary"
														style={{
															fontSize: '11px',
															padding: '8px',
															minWidth: 'auto'
														}}
														title="View Details"
													>
														<Eye size={14} />
													</button>
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			)}

			{/* Purchase Details Modal */}
			{showDetailsModal && selectedPurchase && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 1000,
					padding: '20px'
				}}>
					<div style={{
						backgroundColor: 'white',
						borderRadius: '12px',
						padding: '24px',
						maxWidth: '800px',
						width: '100%',
						maxHeight: '90vh',
						overflowY: 'auto'
					}}>
						{/* Modal Header */}
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '24px',
							paddingBottom: '16px',
							borderBottom: '1px solid #E5E7EB'
						}}>
							<h2 style={{
								fontSize: '20px',
								fontWeight: '600',
								color: '#111827',
								margin: 0
							}}>
								Purchase Details
							</h2>
							<button
								onClick={() => setShowDetailsModal(false)}
								style={{
									background: 'none',
									border: 'none',
									fontSize: '24px',
									cursor: 'pointer',
									color: '#6B7280'
								}}
							>
								×
							</button>
						</div>

						{/* Debug Information - Remove this in production */}
						<div style={{
							backgroundColor: '#f3f4f6',
							border: '1px solid #d1d5db',
							borderRadius: '8px',
							padding: '16px',
							marginBottom: '16px'
						}}>
							<h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
								Debug Info (Remove in production)
							</h4>
							<div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
								<div>AWB Number: {selectedPurchase?.awb_number || 'Not assigned'}</div>
								<div>Seller Email: {selectedPurchase?.seller?.email || 'Not available'}</div>
								<div>Order Status: {selectedPurchase?.status || 'Unknown'}</div>
								<div>Order ID: {selectedPurchase?.order_id || 'Unknown'}</div>
							</div>
						</div>

						{/* Modal Content */}
						<div style={{ display: 'grid', gap: '24px' }}>
							{/* Order Information */}
							<div>
								<h3 style={{
									fontSize: '16px',
									fontWeight: '600',
									color: '#111827',
									marginBottom: '12px'
								}}>
									Order Information
								</h3>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
									gap: '12px'
								}}>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Order ID</label>
										<div style={{ fontSize: '14px', fontFamily: 'monospace' }}>{selectedPurchase.order_id}</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Purchase Status</label>
										<div style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '4px',
											backgroundColor: '#6B7280',
											color: 'white',
											padding: '4px 8px',
											borderRadius: '12px',
											fontSize: '12px',
											fontWeight: '500'
										}}>
											{selectedPurchase.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Dress Status</label>
										<div style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '4px',
											backgroundColor: getDressStatusColor(selectedPurchase.listing?.dress_status_id),
											color: 'white',
											padding: '4px 8px',
											borderRadius: '12px',
											fontSize: '12px',
											fontWeight: '500'
										}}>
											{React.createElement(getDressStatusIcon(selectedPurchase.listing?.dress_status_id), { size: 12 })}
											{getDressStatusName(
												selectedPurchase.listing?.dress_status ||
												selectedPurchase.listing?.dressStatus ||
												selectedPurchase.listing?.dress_status_id ||
												selectedPurchase.listing?.dressStatusId
											)}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Created</label>
										<div style={{ fontSize: '14px' }}>{formatDate(selectedPurchase.created_at)}</div>
									</div>
									{selectedPurchase.awb_number && (
										<div>
											<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>AWB Number</label>
											<div style={{ fontSize: '14px', fontFamily: 'monospace' }}>{selectedPurchase.awb_number}</div>
										</div>
									)}
								</div>
							</div>

							{/* Item Information */}
							<div>
								<h3 style={{
									fontSize: '16px',
									fontWeight: '600',
									color: '#111827',
									marginBottom: '12px'
								}}>
									Item Information
								</h3>
								
								{/* Item Images - Front and Back Only */}
								{selectedPurchase.listing?.dress_images && selectedPurchase.listing.dress_images.length > 0 && (
									<div style={{ marginBottom: '16px' }}>
										<h4 style={{
											fontSize: '14px',
											fontWeight: '600',
											color: '#374151',
											marginBottom: '8px'
										}}>
											Item Images
										</h4>
										<div style={{
											display: 'flex',
											gap: '16px',
											justifyContent: 'flex-start'
										}}>
											{selectedPurchase.listing.dress_images
												.filter(image => ['front_full', 'back_full'].includes(image.image_type))
												.map((image, index) => (
													<div key={index} style={{
														position: 'relative',
														borderRadius: '8px',
														overflow: 'hidden',
														border: '1px solid #E5E7EB',
														boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
													}}>
													<img
														src={getImageUrl(image.medium_path || image.original_path)}
														alt={`${image.image_type} view`}
														style={{
															width: '150px',
															height: '150px',
															objectFit: 'cover',
															display: 'block'
														}}
													/>
														<div style={{
															position: 'absolute',
															bottom: '0',
															left: '0',
															right: '0',
															backgroundColor: 'rgba(0, 0, 0, 0.8)',
															color: 'white',
															padding: '6px 8px',
															fontSize: '11px',
															fontWeight: '600',
															textTransform: 'capitalize',
															textAlign: 'center'
														}}>
															{image.image_type === 'front_full' ? 'Front View' : 'Back View'}
														</div>
													</div>
												))}
										</div>
									</div>
								)}

								{/* Item Details Grid */}
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
									gap: '16px'
								}}>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Item Name</label>
										<div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
											{selectedPurchase.listing?.item_details?.item_name || selectedPurchase.listing?.designer_brand || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Brand/Designer</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.designer_brand || selectedPurchase.listing?.item_details?.brand || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Category</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.category || selectedPurchase.listing?.item_details?.category || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Size</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.item_details?.size || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Colors</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.colors || selectedPurchase.listing?.item_details?.colors || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Fabric</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.fabrics || selectedPurchase.listing?.item_details?.fabric || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Work/Embroidery</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.work_embroidery || selectedPurchase.listing?.item_details?.work_embroidery || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Condition Grade</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.item_details?.condition_grade || 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Original Price</label>
										<div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
											{selectedPurchase.listing?.original_price ? formatCurrency(selectedPurchase.listing.original_price) : 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Purchase Date</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.purchase_date ? formatDate(selectedPurchase.listing.purchase_date) : 'N/A'}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Original Tags Present</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.original_tags_present ? (
												<span style={{ color: '#059669', fontWeight: '500' }}>Yes</span>
											) : (
												<span style={{ color: '#DC2626', fontWeight: '500' }}>No</span>
											)}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Featured</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{selectedPurchase.listing?.is_featured ? (
												<span style={{ color: '#F59E0B', fontWeight: '500' }}>⭐ Featured</span>
											) : (
												<span style={{ color: '#6B7280' }}>No</span>
											)}
										</div>
									</div>
								</div>

								{/* Additional Item Details */}
								{selectedPurchase.listing?.item_details && (
									<div style={{ marginTop: '16px' }}>
										<h4 style={{
											fontSize: '14px',
											fontWeight: '600',
											color: '#374151',
											marginBottom: '8px'
										}}>
											Additional Details
										</h4>
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
											gap: '16px'
										}}>
											{selectedPurchase.listing.item_details.occasion && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Occasion</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.occasion}
													</div>
												</div>
											)}
											{selectedPurchase.listing.item_details.season && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Season</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.season}
													</div>
												</div>
											)}
											{selectedPurchase.listing.item_details.sleeve_length && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Sleeve Length</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.sleeve_length}
													</div>
												</div>
											)}
											{selectedPurchase.listing.item_details.neckline && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Neckline</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.neckline}
													</div>
												</div>
											)}
											{selectedPurchase.listing.item_details.length && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Length</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.length}
													</div>
												</div>
											)}
											{selectedPurchase.listing.item_details.fit && (
												<div>
													<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Fit</label>
													<div style={{ fontSize: '14px', color: '#111827' }}>
														{selectedPurchase.listing.item_details.fit}
													</div>
												</div>
											)}
										</div>
									</div>
								)}

								{/* Defects Information */}
								{selectedPurchase.listing?.condition?.defects && selectedPurchase.listing.condition.defects.length > 0 && (
									<div style={{ marginTop: '16px' }}>
										<h4 style={{
											fontSize: '14px',
											fontWeight: '600',
											color: '#374151',
											marginBottom: '8px'
										}}>
											Defects
										</h4>
										<div style={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: '6px'
										}}>
											{selectedPurchase.listing.condition.defects.map((defect, index) => (
												<span
													key={index}
													style={{
														backgroundColor: '#FEF3C7',
														color: '#92400E',
														padding: '4px 8px',
														borderRadius: '12px',
														fontSize: '12px',
														fontWeight: '500',
														border: '1px solid #FCD34D'
													}}
												>
													{defect}
												</span>
											))}
										</div>
									</div>
								)}
							</div>

							{/* Financial Information */}
							<div>
								<h3 style={{
									fontSize: '16px',
									fontWeight: '600',
									color: '#111827',
									marginBottom: '12px'
								}}>
									Financial Information
								</h3>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
									gap: '12px'
								}}>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Total Amount</label>
										<div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
											{formatCurrency(selectedPurchase.total_amount)}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Deposit Amount</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{formatCurrency(selectedPurchase.deposit_amount)}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Remaining Amount</label>
										<div style={{ fontSize: '14px', color: '#111827' }}>
											{formatCurrency(selectedPurchase.total_amount - selectedPurchase.deposit_amount)}
										</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Payment Status</label>
										<div style={{
											fontSize: '14px',
											color: selectedPurchase.payment_status === 'paid' ? '#059669' : '#DC2626',
											fontWeight: '500'
										}}>
											{selectedPurchase.payment_status?.toUpperCase() || 'N/A'}
										</div>
									</div>
								</div>
							</div>

							{/* Buyer Information */}
							<div>
								<h3 style={{
									fontSize: '16px',
									fontWeight: '600',
									color: '#111827',
									marginBottom: '12px'
								}}>
									Buyer Information
								</h3>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
									gap: '12px'
								}}>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Name</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.buyer?.name || 'N/A'}</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Email</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.buyer?.email || 'N/A'}</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Phone</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.buyer?.phone || 'N/A'}</div>
									</div>
								</div>
							</div>

							{/* Seller Information */}
							<div>
								<h3 style={{
									fontSize: '16px',
									fontWeight: '600',
									color: '#111827',
									marginBottom: '12px'
								}}>
									Seller Information
								</h3>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
									gap: '12px'
								}}>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Name</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.seller?.name || 'N/A'}</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Email</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.seller?.email || 'N/A'}</div>
									</div>
									<div>
										<label style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Phone</label>
										<div style={{ fontSize: '14px' }}>{selectedPurchase.seller?.phone || 'N/A'}</div>
									</div>
								</div>
							</div>

							{/* Status History */}
							{selectedPurchase.status_history && selectedPurchase.status_history.length > 0 && (
								<div>
									<h3 style={{
										fontSize: '16px',
										fontWeight: '600',
										color: '#111827',
										marginBottom: '12px'
									}}>
										Status History
									</h3>
									<div style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '8px'
									}}>
										{selectedPurchase.status_history.map((history, index) => (
											<div key={index} style={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
												padding: '12px',
												backgroundColor: '#F9FAFB',
												borderRadius: '8px'
											}}>
												<div style={{
													width: '8px',
													height: '8px',
													backgroundColor: '#6B7280',
													borderRadius: '50%'
												}} />
												<div style={{ flex: 1 }}>
													<div style={{
														fontSize: '14px',
														fontWeight: '500',
														color: '#111827'
													}}>
														{history.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
													</div>
													{history.notes && (
														<div style={{
															fontSize: '12px',
															color: '#6B7280',
															marginTop: '2px'
														}}>
															{history.notes}
														</div>
													)}
												</div>
												<div style={{
													fontSize: '12px',
													color: '#6B7280'
												}}>
													{formatDate(history.created_at)}
												</div>
											</div>
										))}
									</div>
								</div>
							)}
						</div>

						{/* Modal Footer */}
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginTop: '24px',
							paddingTop: '16px',
							borderTop: '1px solid #E5E7EB'
						}}>
							{/* Left side - Conditional buttons based on dress status */}
							{selectedPurchase?.seller?.email ? (
								<>
									{/* Show "Dispatched to buyer" button only for Store Manager */}
									{user?.role === 'Store Manager' && selectedPurchase?.listing?.dress_status_id === 7 && selectedPurchase?.listing?.status === 7 && (
										<button
											onClick={() => setShowDispatchModal(true)}
											className="btn btn-primary"
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												backgroundColor: '#3b82f6',
												color: 'white',
												border: 'none',
												padding: '10px 16px',
												borderRadius: '6px',
												fontSize: '14px',
												fontWeight: '500',
												cursor: 'pointer',
												transition: 'all 0.2s ease',
												marginRight: '8px'
											}}
										>
											<Truck size={16} />
											Dispatched to Buyer
										</button>
									)}
									{/* Show "Received at Store" button only if dress status is "At Store" (ID: 4) */}
							{user?.role === 'Store Manager' && selectedPurchase?.listing?.dress_status_id === 4 ? (
										<button
											onClick={() => markItemReceived(selectedPurchase.order_id)}
											className="btn btn-success"
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												backgroundColor: '#059669',
												color: 'white',
												border: 'none',
												padding: '10px 16px',
												borderRadius: '6px',
												fontSize: '14px',
												fontWeight: '500',
												cursor: 'pointer',
												transition: 'all 0.2s ease'
											}}
										>
											<CheckCircle size={16} />
											Received at Store
										</button>
							) : selectedPurchase?.listing?.dress_status_id && selectedPurchase?.listing?.dress_status_id <= 3 ? (
										/* Show "Send AWB to Seller" button only for statuses before "In Transit" (ID: 1, 2) */
										<button
											onClick={() => sendAwbEmail(selectedPurchase.order_id)}
											disabled={sendingEmail}
											className="btn btn-primary"
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												backgroundColor: selectedPurchase?.awb_number ? '#059669' : '#F59E0B',
												color: 'white',
												border: 'none',
												padding: '10px 16px',
												borderRadius: '6px',
												fontSize: '14px',
												fontWeight: '500',
												cursor: sendingEmail ? 'not-allowed' : 'pointer',
												opacity: sendingEmail ? 0.6 : 1,
												transition: 'all 0.2s ease'
											}}
										>
											{sendingEmail ? (
												<>
													<RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
													Sending...
												</>
											) : (
												<>
													<Send size={16} />
													{selectedPurchase?.awb_number ? 'Send AWB to Seller' : 'Assign AWB & Send Email to Seller'}
												</>
											)}
										</button>
									) : (
										/* No action button for statuses 4 and above (At Store, Authenticated, etc.) */
										<div style={{
											padding: '10px 16px',
											backgroundColor: '#f3f4f6',
											borderRadius: '6px',
											fontSize: '14px',
											color: '#6b7280'
										}}>
											{selectedPurchase?.listing?.dressStatus?.name || 'No action required'}
										</div>
									)}
								</>
							) : (
								<div style={{
									padding: '10px 16px',
									backgroundColor: '#f3f4f6',
									borderRadius: '6px',
									fontSize: '14px',
									color: '#6b7280'
								}}>
									No seller email available
								</div>
							)}
							
							{/* Right side - Close button */}
							<button
								onClick={() => setShowDetailsModal(false)}
								className="btn btn-secondary"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Dispatch to Buyer Modal - Only for Store Manager */}
			{showDispatchModal && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 1000
				}} onClick={() => !dispatching && setShowDispatchModal(false)}>
					<div style={{
						backgroundColor: 'white',
						borderRadius: '8px',
						padding: '24px',
						width: '90%',
						maxWidth: '500px',
						boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
					}} onClick={(e) => e.stopPropagation()}>
						<h2 style={{
							fontSize: '20px',
							fontWeight: 'bold',
							marginBottom: '20px',
							color: '#111827'
						}}>
							Dispatched to Buyer
						</h2>

						<div style={{ marginBottom: '16px' }}>
							<label style={{
								display: 'block',
								fontSize: '14px',
								fontWeight: '500',
								color: '#374151',
								marginBottom: '6px'
							}}>
								AWB Number *
							</label>
							<input
								type="text"
								value={dispatchForm.awb_number}
								onChange={(e) => setDispatchForm({ ...dispatchForm, awb_number: e.target.value })}
								placeholder="Enter AWB number"
								disabled={dispatching}
								style={{
									width: '100%',
									padding: '10px 12px',
									border: '1px solid #D1D5DB',
									borderRadius: '6px',
									fontSize: '14px',
									outline: 'none',
									transition: 'border-color 0.2s'
								}}
								onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
								onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
							/>
						</div>

						<div style={{ marginBottom: '16px' }}>
							<label style={{
								display: 'block',
								fontSize: '14px',
								fontWeight: '500',
								color: '#374151',
								marginBottom: '6px'
							}}>
								Confirm AWB Number *
							</label>
							<input
								type="text"
								value={dispatchForm.confirm_awb_number}
								onChange={(e) => setDispatchForm({ ...dispatchForm, confirm_awb_number: e.target.value })}
								placeholder="Confirm AWB number"
								disabled={dispatching}
								style={{
									width: '100%',
									padding: '10px 12px',
									border: dispatchForm.awb_number && dispatchForm.confirm_awb_number && dispatchForm.awb_number !== dispatchForm.confirm_awb_number 
										? '1px solid #EF4444' 
										: '1px solid #D1D5DB',
									borderRadius: '6px',
									fontSize: '14px',
									outline: 'none',
									transition: 'border-color 0.2s'
								}}
								onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
								onBlur={(e) => {
									if (dispatchForm.awb_number && dispatchForm.confirm_awb_number && dispatchForm.awb_number !== dispatchForm.confirm_awb_number) {
										e.target.style.borderColor = '#EF4444';
									} else {
										e.target.style.borderColor = '#D1D5DB';
									}
								}}
							/>
							{dispatchForm.awb_number && dispatchForm.confirm_awb_number && dispatchForm.awb_number !== dispatchForm.confirm_awb_number && (
								<p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
									AWB numbers do not match
								</p>
							)}
						</div>

						<div style={{ marginBottom: '20px' }}>
							<label style={{
								display: 'block',
								fontSize: '14px',
								fontWeight: '500',
								color: '#374151',
								marginBottom: '6px'
							}}>
								Courier Provider *
							</label>
							<input
								type="text"
								value={dispatchForm.courier_provider}
								onChange={(e) => setDispatchForm({ ...dispatchForm, courier_provider: e.target.value })}
								placeholder="Enter courier provider name"
								disabled={dispatching}
								style={{
									width: '100%',
									padding: '10px 12px',
									border: '1px solid #D1D5DB',
									borderRadius: '6px',
									fontSize: '14px',
									outline: 'none',
									transition: 'border-color 0.2s'
								}}
								onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
								onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
							/>
						</div>

						<div style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							marginTop: '24px'
						}}>
							<button
								onClick={() => {
									if (!dispatching) {
										setShowDispatchModal(false);
										setDispatchForm({ awb_number: '', confirm_awb_number: '', courier_provider: '' });
									}
								}}
								disabled={dispatching}
								style={{
									padding: '10px 20px',
									border: '1px solid #D1D5DB',
									borderRadius: '6px',
									backgroundColor: 'white',
									color: '#374151',
									fontSize: '14px',
									fontWeight: '500',
									cursor: dispatching ? 'not-allowed' : 'pointer',
									opacity: dispatching ? 0.6 : 1
								}}
							>
								Cancel
							</button>
							<button
								onClick={handleDispatchToBuyer}
								disabled={dispatching || !dispatchForm.awb_number || !dispatchForm.confirm_awb_number || !dispatchForm.courier_provider || dispatchForm.awb_number !== dispatchForm.confirm_awb_number}
								style={{
									padding: '10px 20px',
									border: 'none',
									borderRadius: '6px',
									backgroundColor: dispatching || !dispatchForm.awb_number || !dispatchForm.confirm_awb_number || !dispatchForm.courier_provider || dispatchForm.awb_number !== dispatchForm.confirm_awb_number
										? '#9CA3AF'
										: '#3b82f6',
									color: 'white',
									fontSize: '14px',
									fontWeight: '500',
									cursor: dispatching || !dispatchForm.awb_number || !dispatchForm.confirm_awb_number || !dispatchForm.courier_provider || dispatchForm.awb_number !== dispatchForm.confirm_awb_number
										? 'not-allowed'
										: 'pointer',
									display: 'flex',
									alignItems: 'center',
									gap: '8px'
								}}
							>
								{dispatching ? (
									<>
										<RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
										Dispatching...
									</>
								) : (
									<>
										<Truck size={16} />
										Confirm Dispatch
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PurchasedItems;
